import React, { useState, useEffect } from 'react';

import {
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import {
  Form,
  Input,
  Button,
  App,
  Space,
  Descriptions
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRevalidator } from 'react-router-dom';

import { formatDateTime } from '../../../utils/dateTime';

export default function ProductCategoryShow({
  productCategory: initialProductCategory,
  productCategoryService,
  role: _role = 'operator',
  basePath = '/product_categories'
}) {
  const { t } = useTranslation();
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [productCategory, setProductCategory] = useState(initialProductCategory);

  // Sync loader data changes
  useEffect(() => {
    if (initialProductCategory) {
      setProductCategory(initialProductCategory);
    }
  }, [initialProductCategory]);

  const handleEdit = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    form.resetFields();
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const response = await productCategoryService.update(productCategory.id, values);
      if (response.success) {
        message.success(t('common.message.update_success'));
        setProductCategory(response.data);
        setIsEditMode(false);
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.update_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Update product category error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    modal.confirm({
      title: t('shared.product_categories.detail.delete_confirm'),
      icon: <ExclamationCircleOutlined />,
      content: t('shared.product_categories.detail.delete_confirm_description'),
      okText: t('common.button.confirm'),
      okType: 'danger',
      cancelText: t('common.button.cancel'),
      onOk: async () => {
        setDeleting(true);
        try {
          const response = await productCategoryService.delete(productCategory.id);
          if (response.success) {
            message.success(t('common.message.delete_success'));
            // 由於 basename='/operator'，路徑應該是相對的，不需要加上 /operator
            // 只需要 basePath (例如 '/product_categories')
            console.log('Navigating to:', basePath); // 調試用
            navigate(basePath, { replace: true });
          } else {
            handleApiError(response, { t, fallbackMessage: t('common.error.delete_failed') });
          }
        } catch (error) {
          handleNetworkError(error, { t });
          console.error('Delete product category error:', error);
        } finally {
          setDeleting(false);
        }
      }
    });
  };

  if (!productCategory) {
    return (
      <p>{t('shared.product_categories.detail.not_found')}</p>
    );
  }

  const headerBasePath = _role === 'operator' ? '/operator' : '/admin';

  const headerActions = !isEditMode && (
    <Space>
      <Button
        type="primary"
        icon={<EditOutlined />}
        onClick={handleEdit}
      >
        {t('common.button.edit')}
      </Button>
      <Button
        danger
        icon={<DeleteOutlined />}
        onClick={handleDelete}
        loading={deleting}
      >
        {t('common.button.delete')}
      </Button>
    </Space>
  );

  return (
    <div>
      <ListPageHeader
        basePath={headerBasePath}
        items={['products', 'products.product_categories', { label: productCategory.name }]}
        actions={headerActions}
      />
      {isEditMode ? (
          <Form
            key={isEditMode ? 'edit' : 'view'}
            form={form}
            layout="vertical"
            onFinish={handleSave}
            initialValues={productCategory}
          >
            <Form.Item
              label={t('shared.product_categories.form.name')}
              name="name"
              rules={[
                { required: true, message: t('shared.product_categories.form.name_required') }
              ]}
            >
              <Input placeholder={t('shared.product_categories.form.name_placeholder')} />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  htmlType="submit"
                  loading={saving}
                >
                  {t('common.button.save')}
                </Button>
                <Button
                  icon={<CloseOutlined />}
                  onClick={handleCancel}
                  disabled={saving}
                >
                  {t('common.button.cancel')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        ) : (
          <Descriptions column={1} bordered>
            <Descriptions.Item label={t('shared.product_categories.form.name')}>
              {productCategory.name}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.created_at')}>
              {productCategory.created_at ? formatDateTime(productCategory.created_at) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('common.updated_at')}>
              {productCategory.updated_at ? formatDateTime(productCategory.updated_at) : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}
    </div>
  );
}
